import { api } from '@core/api/http';
import { environment } from '@env/environment';

import { EMPTY_SETTINGS, parseSettingsDocument, type SettingsDocument } from './settings.model';

/**
 * Loading and saving the settings document.
 *
 * The Settings API is not deployed yet, so `environment.settingsApi.useMock`
 * selects an in-memory transport that persists to local storage - changes
 * then live in the browser that made them. Flipping it to false points the
 * same calls at `GET` / `PUT {baseUrl}/settings`, and nothing above this file
 * changes.
 *
 * Because the mock is per-browser, the Settings page is read-only in a
 * production build that is still on it: an admin's save there would quietly
 * change their own portal and nobody else's. `SETTINGS_EDITABLE` says so.
 */

export interface SettingsApi {
  load(): Promise<SettingsDocument>;
  save(document: SettingsDocument): Promise<SettingsDocument>;
}

interface SettingsApiConfig {
  baseUrl?: string;
  useMock?: boolean;
}

const config = (environment as { settingsApi?: SettingsApiConfig }).settingsApi ?? {};

export const SETTINGS_USE_MOCK = config.useMock !== false;

export const SETTINGS_EDITABLE = !(environment.production && SETTINGS_USE_MOCK);

export const SETTINGS_STORAGE_KEY = 'cw-settings';

const mockSettingsApi: SettingsApi = {
  async load() {
    try {
      const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
      return stored ? parseSettingsDocument(JSON.parse(stored)) : EMPTY_SETTINGS;
    } catch {
      return EMPTY_SETTINGS;
    }
  },

  async save(document) {
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(document));
    } catch {
      /* the saved document still applies for this session */
    }

    return document;
  }
};

const httpSettingsApi: SettingsApi = {
  async load() {
    return parseSettingsDocument(await api.get<unknown>(`${(config.baseUrl ?? '').replace(/\/$/, '')}/settings`));
  },

  async save(document) {
    return parseSettingsDocument(await api.put<unknown>(`${(config.baseUrl ?? '').replace(/\/$/, '')}/settings`, document));
  }
};

export const settingsApi: SettingsApi = SETTINGS_USE_MOCK ? mockSettingsApi : httpSettingsApi;
